export interface PDFHighlightRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PDFViewerNavigation {
  pageIndex?: number;
  query?: string;
  highlightRects?: PDFHighlightRect[];
  requestId?: string | number;
}

export interface PDFPageScrollHandle {
  scrollToIndex: (options: {
    index: number;
    align: 'center';
    behavior: 'auto' | 'smooth';
  }) => void;
}

export function parsePDFViewerNavigation(
  searchParams: URLSearchParams
): PDFViewerNavigation | undefined {
  return parsePDFViewerNavigationFromKeys(searchParams, {
    pageIndex: 'pageIndex',
    query: 'query',
    highlightRects: 'highlightRects',
  });
}

export function parseFullscreenPDFViewerNavigation(
  searchParams: URLSearchParams
): PDFViewerNavigation | undefined {
  return parsePDFViewerNavigationFromKeys(searchParams, {
    pageIndex: 'fullscreenAttachmentPageIndex',
    query: 'fullscreenAttachmentQuery',
    highlightRects: 'fullscreenAttachmentHighlightRects',
    requestId: 'fullscreenAttachmentRequestId',
  });
}

function parsePDFViewerNavigationFromKeys(
  searchParams: URLSearchParams,
  keys: {
    pageIndex: string;
    query: string;
    highlightRects: string;
    requestId?: string;
  }
): PDFViewerNavigation | undefined {
  const rawPageIndex = searchParams.get(keys.pageIndex);
  const query = searchParams.get(keys.query) ?? undefined;
  const rawHighlightRects = searchParams.get(keys.highlightRects);
  const requestId = keys.requestId
    ? (searchParams.get(keys.requestId) ?? undefined)
    : undefined;
  const pageIndex =
    rawPageIndex === null || !/^\d+$/.test(rawPageIndex)
      ? undefined
      : Number.parseInt(rawPageIndex, 10);
  const highlightRects = parsePDFHighlightRects(rawHighlightRects);

  if (pageIndex === undefined && !query && !highlightRects) return undefined;

  return {
    pageIndex,
    query,
    highlightRects,
    requestId: requestId ?? searchParams.toString(),
  };
}

function parsePDFHighlightRects(
  rawHighlightRects: string | null
): PDFHighlightRect[] | undefined {
  if (!rawHighlightRects) return undefined;

  try {
    const parsed = JSON.parse(rawHighlightRects);
    if (!Array.isArray(parsed)) return undefined;

    const rects = parsed.filter(isPDFHighlightRect);
    return rects.length ? rects : undefined;
  } catch {
    return undefined;
  }
}

function isPDFHighlightRect(value: unknown): value is PDFHighlightRect {
  if (!value || typeof value !== 'object') return false;

  const rect = value as Record<string, unknown>;
  return (
    isNormalizedRectNumber(rect.x) &&
    isNormalizedRectNumber(rect.y) &&
    isNormalizedRectNumber(rect.width) &&
    isNormalizedRectNumber(rect.height)
  );
}

function isNormalizedRectNumber(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isFinite(value) &&
    value >= 0 &&
    value <= 1
  );
}

export function createPDFViewerNavigationKey(
  navigation: PDFViewerNavigation | undefined
): string | null {
  if (!navigation) return null;

  return [
    navigation.requestId ?? '',
    navigation.pageIndex ?? '',
    navigation.query ?? '',
    navigation.highlightRects
      ?.map(rect => `${rect.x}:${rect.y}:${rect.width}:${rect.height}`)
      .join('|') ?? '',
  ].join('::');
}

export function normalizePDFPageIndex(
  pageIndex: number | undefined,
  pageCount: number
): number | null {
  if (pageIndex === undefined) return null;
  if (!Number.isInteger(pageIndex)) return null;
  if (pageIndex < 0) return null;
  if (pageIndex >= pageCount) return null;
  return pageIndex;
}

export function applyPDFViewerNavigation({
  navigation,
  pageCount,
  pagesScroller,
  thumbnailsScroller,
  onCursorChange,
  logger = console,
}: {
  navigation: PDFViewerNavigation | undefined;
  pageCount: number;
  pagesScroller: PDFPageScrollHandle | null;
  thumbnailsScroller?: PDFPageScrollHandle | null;
  onCursorChange: (index: number) => void;
  logger?: Pick<Console, 'warn'>;
}): boolean {
  if (!navigation) return false;

  const pageIndex = normalizePDFPageIndex(navigation.pageIndex, pageCount);
  if (pageIndex === null) {
    if (navigation.pageIndex !== undefined) {
      logger.warn(
        `Ignored invalid PDF pageIndex ${navigation.pageIndex} for pageCount ${pageCount}`
      );
    }
    return false;
  }
  if (!pagesScroller) return false;

  pagesScroller.scrollToIndex({
    index: pageIndex,
    align: 'center',
    behavior: 'smooth',
  });
  thumbnailsScroller?.scrollToIndex({
    index: pageIndex,
    align: 'center',
    behavior: 'auto',
  });
  onCursorChange(pageIndex);

  return true;
}
